import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Box, ClickAwayListener, Stack } from '@mui/material'
import {useDebounce} from 'use-debounce'
import { useTranslation } from 'react-i18next'
import TopBar from 'packages/ui/Arborist/components/Search/TopBar'
import Tree from 'packages/ui/Arborist/components/Tree'
import TreeSearch from 'packages/ui/Arborist/components/Search/Search'
import convertAccounts from 'packages/ui/Arborist/utils'
import TreeProvider, { useTreeContext} from 'packages/ui/Arborist/TreeProvider'
import { useInPerimeter } from 'packages/ui/Arborist/utils'

import Popper from './Popper'

function AccountsPerimeter({onChange, hasError, onValidationClear}) {
  const {accounts, waiting, treeRef, onNodeRemove, onNodeAdd} = useTreeContext()
  const [anchorEl, setAnchorEl] = useState(null)
  const [_searchVerbatim, setSearchVerbatim] = useState('')
  const [searchVerbatim] = useDebounce(_searchVerbatim, 1000)
  const checkedAccounts = useInPerimeter()
  const searchInputRef = useRef(null)
  const searchFieldRef = useRef(null)
  const { t } = useTranslation()
  const tree = useMemo(() => {
    return convertAccounts({ accounts})
  }, [accounts])

  useEffect(() => {
    return () => {
      setAnchorEl(null)
      setSearchVerbatim('')
    }
  }, [])

  const handleSiteSelect = async (node, checked) => {
    if (checked) {
      onChange(node.data.data)
      onValidationClear()
    } else {
      onChange(null)
    }
  }

  const handleNodeSelect = async (node) => {
    const checked = checkedAccounts.findIndex(account => account.sf_id === node.data.data.sf_id) > -1
    if (!checked) {
      await onNodeAdd(node)
      onChange(node.data.data)
      onValidationClear()
    } else {
      onNodeRemove(node)
      onChange(null)
    }
  }

  return (
    <Box
      sx={{ 
        maxHeight: '44px', 
        minHeight: '44px', 
        maxWidth: '100%',
        width: '100%',
        cursor: waiting ? 'wait' : 'inherit',
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: hasError ? '#f44336' : '#EFEFEF'
          },
          '&:hover fieldset': {
            borderColor: hasError ? '#f44336' : '#EFEFEF'
          },
          '&.Mui-focused fieldset': {
            borderColor: hasError ? '#f44336' : '#2570EB'
          }
        }
      }}
      ref={searchInputRef}
      onClick={() => {
        setAnchorEl(searchInputRef.current)
      }}
    >
      <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
        <Stack>
          <TopBar
            showCountChip={true}
            showUnfolded={false}
            onSearchChange={setSearchVerbatim}
            searchFieldRef={searchFieldRef}
            placeholder={t('ui_elements.search_site')}
          />
          {
            searchVerbatim.length > 0
              ? (
                <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} width='432px'>
                  <Box
                    sx={{
                      minWidth: '200px',
                      minHeight: '200px',
                      position: 'relative',
                    }}
                  >
                  <TreeSearch
                    hideTopBar={true}
                    ref={treeRef}
                    name={searchVerbatim}
                    height={230} 
                    onNodeSelect={handleSiteSelect}
                    onlySite
                  />
                  </Box>
                </Popper>

              )
              : (
              <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} width='432px'>
                <Box
                  sx={{
                    minWidth: '200px',
                    minHeight: '200px',
                    position: 'relative',
                  }}
                >
                  <Tree
                    ref={treeRef}
                    data={tree}
                    openByDefault={true}
                    onlySite={true}
                    height={230}
                    onNodeSelect={handleNodeSelect}
                    onParentNodeSelect={() => {}}
                  />
                </Box>
              </Popper>
              )
          }
        </Stack>
      </ClickAwayListener>
    </Box>
  )
}
export default ({ initialSelection, ...props }) => (
  <TreeProvider onlySite initialSelection={initialSelection}>
    <AccountsPerimeter {...props} />
  </TreeProvider>
)
